import { forwardRef, type AriaRole, type ButtonHTMLAttributes, type ReactNode } from 'react';

type Tone = 'default' | 'success' | 'warning' | 'danger' | 'info';

export function PageHeader({
  title,
  eyebrow,
  description,
  actions,
}: {
  title: string;
  eyebrow?: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div className="min-w-0">
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1 className="page-title">{title}</h1>
        {description && <p className="page-description">{description}</p>}
      </div>
      {actions && <div className="page-header-actions shrink-0">{actions}</div>}
    </header>
  );
}

export function Card({
  children,
  className = '',
  muted = false,
}: {
  children: ReactNode;
  className?: string;
  muted?: boolean;
}) {
  return (
    <section className={`ui-card ${muted ? 'ui-card-muted' : ''} ${className}`}>{children}</section>
  );
}

export function HelperText({
  children,
  tone = 'default',
  className = '',
  role,
}: {
  children: ReactNode;
  tone?: Tone;
  className?: string;
  role?: AriaRole;
}) {
  const icon = tone === 'success' ? '✓' : tone === 'warning' ? '!' : tone === 'danger' ? '!' : 'i';
  return (
    <p role={role} className={`helper-text helper-${tone} ${className}`}>
      <span aria-hidden>{icon}</span>
      <span>{children}</span>
    </p>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <div className="empty-state-icon" aria-hidden>
        ⌁
      </div>
      <p className="font-semibold text-slate-800">{title}</p>
      {description && (
        <p className="mt-1 max-w-sm text-sm leading-5 text-slate-600">{description}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Skeleton({ className = '' }: { className?: string }) {
  return <span className={`skeleton ${className}`} aria-hidden />;
}

export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  }
>(function Button({ variant = 'primary', className = '', ...props }, ref) {
  return <button ref={ref} className={`button button-${variant} ${className}`} {...props} />;
});

export function FilterChipGroup<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  className = '',
}: {
  value: T;
  options: { value: T; label: string; count?: number }[];
  onChange: (value: T) => void;
  ariaLabel: string;
  className?: string;
}) {
  return (
    <div className={`filter-chip-group ${className}`} role="group" aria-label={ariaLabel}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          data-form-value-change={value === option.value ? undefined : 'true'}
          onClick={() => onChange(option.value)}
          className={value === option.value ? 'is-selected' : ''}
          aria-pressed={value === option.value}
        >
          <span>{option.label}</span>
          {option.count !== undefined && <span className="filter-chip-count">{option.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  className = '',
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  ariaLabel: string;
  className?: string;
}) {
  return (
    <div className={`segmented-control ${className}`} role="group" aria-label={ariaLabel}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          data-form-value-change={value === option.value ? undefined : 'true'}
          onClick={() => onChange(option.value)}
          className={value === option.value ? 'is-selected' : ''}
          aria-pressed={value === option.value}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
